import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  can,
  reminderStepLabel,
  type InvoicePolicy,
  SUPERSEDED_REMINDER,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { useSession } from '../session';
import { idem } from '../../lib/idempotency';
import { SectionCard } from '../ui/Detail';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import {
  awaitingServer,
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { usePendingOverride } from '../ui/usePendingOverride';
import { Field, field } from '../create/Field';
import { HundredthsInput, NumberInput, OffsetLinesInput } from '../ui/NumberInput';
import { IconMoney } from '../icons';
import { money } from '../money';

/**
 * Getting paid (PRD v2 §2.2) — late fees and reminders, the two things Servwell does to a
 * shop's own customers automatically.
 *
 * Both switches start OFF and stay off until somebody here turns them on. Everything about
 * this card is built around one rule: an owner can see exactly what tonight's run will do
 * BEFORE it does it. The preview underneath is a real dry run of the real sweep, not a
 * description of one.
 */
export function GettingPaidCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const role = useSession().role;
  const q = useQuery(trpc.collections.policy.queryOptions());
  const preview = useQuery(trpc.collections.preview.queryOptions());
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  /**
   * What you just changed, until the server catches up — an override, not a mirror.
   *
   * 🔴 The mirror it replaces (`useEffect` → `setDraft(q.data.policy)`) had an `onError`
   * that only set a message, so a refused save left the late-fee percentage on screen at the
   * number the server had just rejected. On THIS card that is a figure a shop charges its
   * own customers, and nothing refetched to correct it.
   */
  const override = usePendingOverride<InvoicePolicy>();

  const save = useMutation(
    trpc.collections.updatePolicy.mutationOptions({
      /**
       * 🔴 DO NOT `await` THE INVALIDATION HERE.
       *
       * An earlier version did, to avoid a flash of the stale cached value between the save
       * landing and the refetch arriving. query-core awaits `onSuccess` BEFORE dispatching
       * `success`, so the mutation stays `pending` for the whole await — and with
       * `networkMode: 'offlineFirst'` plus `retry: 1`, a refetch that pauses (signal drops, or
       * the tab blurs) never settles. The mutation therefore never settles either:
       * `awaitingServer()` stays true and EVERY control on this card is disabled until the
       * connection returns.
       *
       * That is precisely the freeze `awaitingServer`'s own docblock says it exists to prevent,
       * reintroduced through a path the classifier cannot see — the pause is on a QUERY while
       * `isPaused` on the MUTATION is false. A write's lifecycle must not depend on a read.
       *
       * The flash is instead handled by `settled` below: an override whose value the server has
       * caught up on is dropped at RENDER time, so there is no moment where the old value shows.
       */
      onSuccess: () => {
        setError(null);
        void qc.invalidateQueries({ queryKey: trpc.collections.policy.queryKey() });
      },
      onError: (e) => {
        setError(e.message);
        override.clear();
        void qc.invalidateQueries({ queryKey: trpc.collections.policy.queryKey() });
      },
    }),
  );

  const runNow = useMutation(
    trpc.collections.runSweep.mutationOptions({
      onSuccess: () => {
        setConfirming(false);
        setError(null);
        // Bare and unscoped ON PURPOSE, unlike `save` above: running the sweep for real
        // charges fees and sends mail, so it moves invoices, payments, reminders and the
        // preview at once. Naming those keys here would be a list to keep in step with the
        // sweep.
        void qc.invalidateQueries();
      },
      onError: (e) => {
        setError(e.message);
        // Close the confirm row too. Leaving it open under an error notice at the top of the
        // card invites a second press of a button that has already charged, or not.
        setConfirming(false);
      },
    }),
  );

  /**
   * `office` holds `invoice:update` and so may set all of this; `bookkeeper` holds
   * `invoice:read` and may not. Without the gate a bookkeeper is shown a live late-fee
   * editor and a "Yes, do it now" button that charges real money — every one of which 403s.
   */
  const mayEdit = can(role, 'invoice', 'update');

  const state = shownState(q, q.data);
  /**
   * ⚠️ Gated on HAVING NOTHING TO DRAW, not on the phase.
   *
   * 🔴 An earlier version said `state !== 'ready' || !q.data`. query-core keeps `data` on a
   * background error, so a refetch that failed on window focus — 30s `staleTime`, one flaky
   * Wi-Fi moment — replaced switches that were correct and on screen a second ago with
   * "Couldn't load". This card previously rendered its cached draft through exactly that
   * case, so the contract REGRESSED it.
   *
   * An error is only a reason to blank a surface when there is nothing left to show. The
   * phase still chooses the WORDS; data presence chooses whether to speak at all.
   */
  if (!q.data) {
    return (
      <SectionCard id={id} title="Getting paid">
        <UnresolvedState
          state={unresolved(state)}
          what="how this shop chases money"
          icon={IconMoney}
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      </SectionCard>
    );
  }

  const server = q.data.policy;
  const busy = awaitingServer(save);
  // Un-answered local writes over the server's row — see `usePendingOverride` for why this
  // is stamped against the watermark rather than compared by value.
  const draft = override.apply(server, q.dataUpdatedAt, busy);
  const emailOn = q.data.emailConfigured;
  const set = (patch: Partial<InvoicePolicy>) => {
    override.record(patch, q.dataUpdatedAt);
    save.mutate({ ...patch, ...idem() });
  };
  const locked = busy || !mayEdit;
  // Text boxes get the permission gate only — `busy` on an input steals the keystrokes the
  // reader is typing into the NEXT field. See the note in AutomationsCard.
  const readOnly = !mayEdit;

  const previewState = shownState(preview, preview.data);
  const fees = preview.data?.fees ?? [];
  const reminders = (preview.data?.reminders ?? []).filter(
    (r) => r.detail !== SUPERSEDED_REMINDER,
  );
  /**
   * What the button will actually DO, which is not the same as what is on the list.
   *
   * A reminder the sweep will SKIP (no email address, mail not configured) is still a row
   * worth showing — but counting it into "and send 3 reminders" promises three messages that
   * two customers will never get. Only `'due'` is a message that is going to be attempted.
   */
  const sendable = reminders.filter((r) => r.status === 'due');

  return (
    <SectionCard id={id} title="Getting paid">
      <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <p className="mb-3 text-sm text-muted-foreground">
        What happens on its own when an invoice goes unpaid. Both of these start switched
        off — nothing gets charged or emailed until you say so here.
      </p>

      {/* ── Late fees ─────────────────────────────────────────────────────── */}
      <div className="border-t border-border pt-3">
        <label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            className="size-4"
            checked={draft.lateFeeEnabled}
            disabled={locked}
            onChange={(e) => set({ lateFeeEnabled: e.target.checked })}
          />
          Charge a late fee on overdue invoices
        </label>

        {draft.lateFeeEnabled ? (
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <Field label="How much">
              <div className="flex gap-2">
                <select
                  className={field}
                  aria-label="Late fee type"
                  value={draft.lateFeeKind}
                  disabled={locked}
                  onChange={(e) =>
                    set({ lateFeeKind: e.target.value as 'flat' | 'percent' })
                  }
                >
                  <option value="percent">% of what's owed</option>
                  <option value="flat">A flat amount</option>
                </select>
                {draft.lateFeeKind === 'percent' ? (
                  <HundredthsInput
                    value={draft.lateFeePercentBps}
                    label="Late fee percent"
                    disabled={readOnly}
                    onCommit={(bps) => set({ lateFeePercentBps: bps })}
                  />
                ) : (
                  <HundredthsInput
                    value={draft.lateFeeFlatCents}
                    label="Late fee amount"
                    decimals={2}
                    disabled={readOnly}
                    onCommit={(cents) => set({ lateFeeFlatCents: cents })}
                  />
                )}
              </div>
            </Field>

            <Field label="Days to wait after the due date">
              <NumberInput
                value={draft.lateFeeGraceDays}
                label="Grace days"
                disabled={readOnly}
                onCommit={(n) => set({ lateFeeGraceDays: n })}
              />
            </Field>

            <Field label="Charge it again every (days) — 0 to charge once">
              <NumberInput
                value={draft.lateFeeIntervalDays}
                label="Days between fees"
                disabled={readOnly}
                onCommit={(n) => set({ lateFeeIntervalDays: n })}
              />
            </Field>

            <Field label="Stop after this many fees">
              <NumberInput
                value={draft.lateFeeMaxCount}
                label="Maximum fees"
                disabled={readOnly}
                onCommit={(n) => set({ lateFeeMaxCount: n })}
              />
            </Field>

            <Field label="Skip balances under">
              <HundredthsInput
                value={draft.lateFeeMinBalanceCents}
                label="Minimum balance"
                decimals={2}
                disabled={readOnly}
                onCommit={(cents) => set({ lateFeeMinBalanceCents: cents })}
              />
            </Field>

            <div className="sm:col-span-2">
              <Notice tone="info">
                A percent fee is taken on what's still owed, not on the original invoice —
                so a customer who paid most of it gets charged on the rest. Every fee
                shows on the invoice before it's added, and you can take any of them back
                off.
              </Notice>
            </div>
          </div>
        ) : null}
      </div>

      {/* ── Reminders ─────────────────────────────────────────────────────── */}
      <div className="mt-3 border-t border-border pt-3">
        <label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            className="size-4"
            checked={draft.remindersEnabled}
            disabled={locked}
            onChange={(e) => set({ remindersEnabled: e.target.checked })}
          />
          Email the customer when an invoice is overdue
        </label>

        {draft.remindersEnabled ? (
          <div className="mt-2 space-y-2">
            {!emailOn ? (
              <Notice tone="warn">
                Email isn't switched on for this account yet, so reminders will be
                recorded but not delivered. They'll start going out the day it is.
              </Notice>
            ) : null}
            <Field label="Send on these days (relative to the due date, one per line)">
              <OffsetLinesInput
                value={draft.reminderOffsetDays}
                label="Reminder schedule"
                disabled={readOnly}
                onCommit={(days) => set({ reminderOffsetDays: days })}
              />
            </Field>
            <p className="text-xs text-muted-foreground">
              {draft.reminderOffsetDays.map((d) => reminderStepLabel(d)).join(' · ')}. A
              negative number nudges them <em>before</em> it's due. However far behind an
              invoice gets, only one reminder goes out per night.
            </p>
          </div>
        ) : null}
      </div>

      {/* ── The preview: what tonight actually does ───────────────────────── */}
      {draft.lateFeeEnabled || draft.remindersEnabled ? (
        <div className="mt-3 border-t border-border pt-3">
          <div className="mb-1 flex items-center justify-between gap-2">
            {/*
              SCOPED, deliberately. "What happens tonight" is answered once on this page, by
              Automations, over BOTH sweeps — this card only ever knew the invoice half, and
              two headings asking one question is how they came to disagree. What belongs
              here is the half this card's switches control and its button acts on.
            */}
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Tonight, on your invoices
            </p>
            <Button variant="ghost" onClick={() => void preview.refetch()}>
              Check again
            </Button>
          </div>
          {/* Its own notice, not the card's: this section has its own query, its own
              heading and its own retry, and the card's line at the top says nothing about
              whether tonight's dry run came back. */}
          <StaleNotice
            show={refreshFailed(preview, preview.data)}
            onRetry={() => void preview.refetch()}
          />
          {!preview.data ? (
            // The same contract as every other card on this page. `ErrorState`'s own copy
            // already carries the reassurance the bespoke notice here used to add by hand
            // ("Your data is safe"), which is the thing a shop needs to know about a dry
            // run that did not come back: nothing has been charged.
            <UnresolvedState
              state={unresolved(previewState)}
              what="tonight's run"
              icon={IconMoney}
              error={preview.error}
              onRetry={() => void preview.refetch()}
              skeleton="h-10"
            />
          ) : fees.length === 0 && reminders.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing. No fee is due and no reminder is owed.
            </p>
          ) : (
            <ul className="space-y-1 text-sm">
              {fees.map((f) => (
                <li key={`f${f.invoiceId}`} className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone="danger">Late fee</StatusBadge>
                  <span className="font-medium tabular-nums">{money(f.amountCents)}</span>
                  <span className="text-muted-foreground">
                    to {f.customerName}
                    {f.invoiceNumber ? ` · invoice #${f.invoiceNumber}` : ''}
                  </span>
                </li>
              ))}
              {reminders.map((r) => (
                <li
                  key={`r${r.invoiceId}${r.offsetDays}`}
                  className="flex flex-wrap items-center gap-2"
                >
                  {/* `'due'`, not `'sent'`. This is a PREVIEW of tonight — the server
                      cannot know a send will succeed, and it used to say so anyway. */}
                  <StatusBadge tone={r.status === 'due' ? 'info' : 'neutral'}>
                    {r.status === 'due' ? 'Reminder' : 'Not sent'}
                  </StatusBadge>
                  <span className="text-muted-foreground">
                    {r.customerName}
                    {r.toEmail ? ` · ${r.toEmail}` : ''}
                    {r.detail ? ` · ${r.detail}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* "Don't wait for tonight." Safe to offer only because the exact list of what
              it will do is sitting directly above it — and every fee it charges can be
              taken back off from the invoice. */}
          {/*
            ⚠️ Gated on `ready`, not merely on the arrays being non-empty. A preview that
            failed leaves both empty, which hides the button correctly by accident — and a
            STALE one would offer to charge a list that is no longer what tonight holds.
          */}
          {previewState === 'ready' &&
          mayEdit &&
          (fees.length > 0 || sendable.length > 0) ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {confirming ? (
                <>
                  <span className="text-sm">
                    Charge {fees.length === 1 ? 'this fee' : `these ${fees.length} fees`}
                    {/*
                      `sendable`, not `reminders`. The list above deliberately shows rows the
                      sweep will SKIP — no address on file, mail not switched on — and
                      counting those into "send 3 reminders" promises a shop three messages
                      that two of their customers will never receive.
                    */}
                    {sendable.length > 0
                      ? ` and send ${sendable.length === 1 ? 'the reminder' : `${sendable.length} reminders`}`
                      : ''}{' '}
                    now?
                  </span>
                  <Button
                    disabled={awaitingServer(runNow)}
                    onClick={() => runNow.mutate({ dryRun: false, ...idem() })}
                  >
                    {awaitingServer(runNow) ? 'Running…' : 'Yes, do it now'}
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirming(false)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Button variant="ghost" onClick={() => setConfirming(true)}>
                  Do it now instead of waiting
                </Button>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </SectionCard>
  );
}

/** A whole-number input that commits on blur — typing "1" on the way to "14" must not save. */
